import {useParams} from "react-router";
import {useQuery} from "@tanstack/react-query";
import WorkScheduleService from "../../../../../../../services/web/work-schedule-service.js";
import {useEffect, useMemo, useRef, useState} from "react";
import {navigateHook} from "../../../../../../../hooks/use-navigate.js";
import projectUtils from "../../../../../../../utils/project-utils.js";
import TemplateHeader from "../../../../../../../components/template/template-header.jsx";
import TemplateTitle from "../../../../../../../components/template/template-title.jsx";
import TemplateBody from "../../../../../../../components/template/template-body.jsx";
import {Template} from "../../../../../../../components/template/template.jsx";
import {Card} from "../../../../../../../components/card/card.jsx";
import clsx from "clsx";
import aggridUtils from "../../../../../../../utils/aggrid-utils.js";
import {AgGridReact} from "ag-grid-react";
import Button from "../../../../../../../components/button/button.jsx";
import styles from './work-schedule-conflicts.module.css';
import {translatedKeys} from "../../utils/work-schedule-i18n.js";
import {groupConflicts, handleWithoutSubstitute, updateWithSubstitute} from "./utils/handlers.js";
import ModalConflictsResolution from "./components/modal-conflicts-resolution.jsx";
import {toast} from "react-toastify";
import {FaCircleCheck, FaCircleExclamation} from "react-icons/fa6";
import {formatIsoDate} from "../../utils/work-schedule-utils.js";

let handleNotifyStorageWarning = () => {
    const warning = localStorage.getItem('warning');
    if (warning) {
        toast.warning(warning)
        localStorage.removeItem('warning');
    }
}

handleNotifyStorageWarning();
const WorkScheduleConflicts = () => {
    const {id} = useParams();
    const [isLoading, setIsLoading] = useState(false)
    const [selectedType, setSelectedType] = useState('effective');
    const [generalColumnDefs, setGeneralColumnDefs] = useState([]);
    const [detailedColumnDefs, setDetailedColumnDefs] = useState([]);
    const [modalConflicts, setModalConflicts] = useState(false);
    const [conflictSelected, setConflictSelected] = useState();
    const [substitutesSelected, setSubstitutesSelected] = useState([])
    const [substituteTableData, setSubstituteTableData] = useState([]);
    const [resolvedConflicts, setResolvedConflicts] = useState([])
    const [dataByDay, setDataByDay] = useState([]);
    const [dataByEffective, setDataByEffective] = useState([]);
    const [canSave, setCanSave] = useState(false);
    const [totalConflicts, setTotalConflicts] = useState(0);
    const [conflictsById, setConflictsById] = useState([]);
    const [workSchedule, setWorkSchedule] = useState({});
    const navigate = navigateHook();

    const defaultColDef = useMemo(() => {
        return {
            filter: "agTextColumnFilter",
            filterParams: aggridUtils.filterParams,
            floatingFilter: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    const conflictsTableRef = useRef(null);
    const substituteTableRef = useRef(null);

    useEffect(() => {
        if (substituteTableRef.current) {
            substituteTableRef.current.api?.refreshCells({ force: true });
        }
    }, [substituteTableData]);

    useEffect(() => {
        if (conflictsTableRef.current) {
            conflictsTableRef.current.api?.refreshCells({ force: true });
        }
    }, [dataByEffective, dataByDay]);

    const getBaseColumnDefs = (location) => {
        if (location === 'general') {
            return [
                {
                    colId: "conflictsCount", headerName: "Conflitos", flex: 1, filter: "agNumberColumnFilter",
                    valueGetter: (params) => Number( params.data.conflicts.length - params.data.conflicts.filter(el => el.substitute).length)
                },
            ]
        }

        return [
            {headerName: "Conflito", field: "id", hide: true},
            {headerName: "Efetivo", field: "effectiveName", sortable: true, flex: 1},
            {
                headerName: "Motivo", field: "observation", sortable: true, flex: 1,
                valueGetter: params => {
                    const obs = params.data.observation;

                    if (!obs) return "";

                    return obs
                      .split(",")
                      .map(item => item.trim())
                      .map(item => translatedKeys[item] ?? item)
                      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
                      .join(", ");
                }
            },
            {headerName: "Substituído por", field: "substitute", sortable: true, flex: 1},
            {headerName: "", field: "conflictStatus", filter: false, sortable: false, width: 60, lockPosition: 'right', suppressMovable: true,
                cellRenderer: (params) => {
                    return params.data.substitute ? <div className="w-full h-full flex justify-center items-center"><FaCircleCheck color="#31b131" size={18}/></div>
                        :  <div className="w-full h-full flex justify-center items-center"><FaCircleExclamation color="#e6b800" size={18}/></div>
                }}
        ];
    }

    useEffect(() => {
        if (selectedType === "effective") loadByEffectiveColumns();
        if (selectedType === "day")  loadByDayColumns();
    }, [selectedType]);

    useEffect(()=> {
        if(resolvedConflicts.length > 0){
            setCanSave(true);
        }
    }, [resolvedConflicts])

    useQuery({
        queryFn: async () => {
            const {data} = await WorkScheduleService.getConflicts(id)
            setDataByEffective(
              data.byEffective.map(el => ({
                  ...el,
                  conflicts: groupConflicts(el.conflicts)
              }))
            );

            setDataByDay(
              data.byDate.map(el => ({
                  ...el,
                  conflicts: groupConflicts(el.conflicts)
              }))
            );

            let conflicts = 0
            for (let i = 0; i < data.byEffective?.length; i++) {
                conflicts += data.byEffective[i].conflicts.length;
            }
            setTotalConflicts(conflicts);
            return data;
        },
        queryKey: ["conflicts", id],
        enabled: !!id
    });

    useQuery({
        enabled:!!id,
        queryKey: ['schedule-work-schedule'],
        queryFn: () =>  WorkScheduleService.getWorkSchedule(id)
            .then(({data}) => {
                if(data)    setWorkSchedule(data.workSchedule);
                return data ? data.workSchedule : {};
            }),
    });

    const loadByEffectiveColumns = () => {
        setSubstituteTableData([])

        const generalDef = getBaseColumnDefs("general")
        generalDef.unshift(
            {headerName: "Efetivo", field: "effectiveName", sortable: true, flex: 1},
            {headerName: "Equipe", field: "team", sortable: true, flex: 1}
        )


        setGeneralColumnDefs(generalDef);

        const detailedDef = getBaseColumnDefs("detailed")
        detailedDef.unshift(
            {
                headerName: "Data", field: "startDate", sortable: false, width: 150, sort: 'asc',
                valueGetter: params => formatIsoDate(params.data.startDate),
            }
        )

        setDetailedColumnDefs(detailedDef);
    }

    const loadByDayColumns = () => {
        setSubstituteTableData([])

        const generalDef = getBaseColumnDefs("general")
        generalDef.unshift(
            {
                headerName: "Data", field: "date", sortable: true, flex: 1,
                valueGetter: params => formatIsoDate(params.data.date),
            }
        )

        setGeneralColumnDefs(generalDef);

        const detailedDef = getBaseColumnDefs("detailed")
        detailedDef.unshift(
            {
                width: 40,
                filter: false,
                headerTooltip: "Selecionar todos os conflitos",
            },
            {
                headerName: "Equipe",
                field: "team",
                sortable: true,
                flex: 1,
            }
        )

        setDetailedColumnDefs(detailedDef)
    }

    const populateSubstituteTable = ({data, node}) => {
        if (node.isSelected()) {
            const {conflicts} = data;
            setSubstituteTableData(conflicts);
            substituteTableRef.current.api.refreshCells({
                force: true,
            });
        } else {
            setConflictSelected(null)
        }
    }
    const onRowClickedOpenModal = (row) => {
        const {node, data} = row
        node.setSelected(true);

        if (node.isSelected()) {
            setConflictSelected(data.id)
            setSubstitutesSelected((prevItems) => [...prevItems, data]);
        } else {
            setConflictSelected(null)
            setSubstitutesSelected((prevItems) => prevItems.filter((item) => item.id !== data.id));
        }

        setModalConflicts(true);
    }

    const saveConflicts = () => {
        const dataToSend = {
            workScheduleId: parseInt(id),
            resolveConflicts: resolvedConflicts
        }

        WorkScheduleService.resolveConflicts(dataToSend)
            .then(({data}) => {
                const {hasConflictUnresolved, hasConflictByWorkSchedule} = data;
                let messageWarning = "Ainda há conflitos a serem solucionados";
                if (hasConflictUnresolved) {
                    //     "Não foi possível solucionar alguns conflitos, pois o efetivo não está mais disponível na data referente";
                    localStorage.setItem('warning', messageWarning);
                    window.location.reload();
                } else if (hasConflictByWorkSchedule) {
                    localStorage.setItem('warning', messageWarning);
                    window.location.reload();
                } else {
                    projectUtils.success(true)
                    navigate(`/work-schedule/form/${id}`, false)
                }
            }).catch(error => {
            console.error(error);
            projectUtils.swalErrorMessage('', "Não foi possível salvar.", 'Ok');
        })
    }

    useEffect(() => {
        if (dataByEffective.length === 0) return;
        const fetchConflicts = async () => {
            setIsLoading(true);

            try {
                const allConflicts = dataByEffective.flatMap(effective => effective.conflicts);
                const conflictPromises = allConflicts.map(async (conflict) => {
                    try {
                        const response = await WorkScheduleService.getAvailableUsersByConflict(conflict.id);
                        return {
                            id: conflict.id,
                            date: conflict.startDate,
                            availableEffective: response.data
                        };
                    } catch (error) {
                        console.error(`Erro ao buscar dados para conflito ${conflict.id}`, error);
                        return null;
                    }
                });
                const results = await Promise.all(conflictPromises);
                const validResults = results.filter(result => result !== null);
                setConflictsById(validResults);

            } catch (err) {
                console.error("Erro geral na busca de conflitos", err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchConflicts();

    }, [dataByEffective]);

    const updateTable = (data, conflicts) => {
        updateWithSubstitute({data,
            conflicts,
            setResolvedConflicts,
            setSubstituteTableData,
            setSubstitutesSelected,
            selectedType,
            setDataByDay,
            setDataByEffective});
    }

    const updateTableWithNoReplace = (conflicts) => {
        handleWithoutSubstitute({ conflicts,
            setResolvedConflicts,
            setSubstituteTableData,
            setSubstitutesSelected,
            selectedType,
            setDataByDay,
            setDataByEffective})
    }

    function redirectToBatchPage() {
        localStorage.setItem("substituteTableData", JSON.stringify(substituteTableData))
        localStorage.setItem("conflictsById", JSON.stringify(conflictsById))
        localStorage.setItem("workScheduleName", workSchedule.name ?? "")
        navigate(`/work-schedule/batch-conflicts/${id}`, false)
    }

    const handleDelete = () => {
        if(!id) return;

        projectUtils.swalMessage(
            'warning',
            'Excluir',
            `Excluir a escala "${workSchedule.name}"?`,
            'Sim',
            true,
            'Não',
            () => {
                WorkScheduleService.delete(id)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso")
                        navigate("/work-schedule", false)
                    })
                    .catch(async error => {
                        console.error(error);
                        let message = 'Não foi possível excluir.';
                        projectUtils.swalErrorMessage('', message, 'Ok');
                    });
            }
        );
    }

    return(
        <Template isLoading={isLoading}>
            <TemplateHeader>
                <TemplateTitle>
                    Conflitos - {workSchedule.name}
                </TemplateTitle>
            </TemplateHeader>
            <TemplateBody>
                <Card>
                    <Card.Body>
                        <div className={"flex flex-1 items-center gap-2"}>
                            <h5 className="text-[14.4px] text-[#676a6c] font-bold">Agrupamento:</h5>
                            <div className="flex w-3/10">
                                <div className={clsx(
                                    "rounded-l-[3px] -mr-px cursor-pointer bg-[#6c757d] text-white uppercase font-bold w-1/3 text-center py-[3px] border border-[#6c757d] hover:opacity-80",
                                    {
                                        "bg-[#ccd4de] text-[#063a6c] border-[#ccd4de]": selectedType === "effective"
                                    }
                                )}
                                     onClick={() => setSelectedType('effective')}>
                                    Por efetivo
                                </div>
                                <div className={clsx(
                                    "rounded-r-[3px] cursor-pointer bg-[#6c757d] text-white uppercase font-bold w-1/3 text-center py-[3px] border border-[#6c757d] hover:opacity-80",
                                    {
                                        "bg-[#ccd4de] text-[#063a6c] border-[#ccd4de]": selectedType === "day"
                                    }
                                )}
                                     onClick={() => setSelectedType('day')}>
                                    Por dia
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <div className="flex gap-2 w-full mt-2">
                    <Card className="flex-1">
                        <Card.Header>
                            <Card.Title>Lista de conflitos</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div style={{height: 'calc(100vh - 243px)'}}>
                                <AgGridReact
                                    ref={conflictsTableRef}
                                    rowData={selectedType === 'effective' ? dataByEffective : dataByDay}
                                    onRowSelected={populateSubstituteTable}
                                    suppressCellFocus={true}
                                    rowSelection={{
                                        mode: 'singleRow',
                                        checkboxes: false,
                                        headerCheckbox: false,
                                        enableClickSelection: true
                                    }}
                                    columnDefs={generalColumnDefs}
                                    defaultColDef={defaultColDef}
                                    animateRows={false}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    theme={aggridUtils.generalTheme({rowHeight: 40, headerHeight: 40})}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                    <Card className="flex-1">
                        <Card.Header>
                            <Card.Title>Resolução de conflitos</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div className={styles.custom} style={{height: 'calc(100vh - 290px)'}}>
                                <AgGridReact
                                    ref={substituteTableRef}
                                    rowData={substituteTableData}
                                    columnDefs={detailedColumnDefs}
                                    defaultColDef={defaultColDef}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    rowSelection={{
                                        mode: 'singleRow',
                                        checkboxes: false,
                                        headerCheckbox: false,
                                        enableClickSelection: true
                                    }}
                                    suppressHeaderFocus={true}
                                    onRowClicked ={onRowClickedOpenModal}
                                    gridOptions={{localeText: {...aggridUtils.localization(), noRowsToShow: 'Selecione um conflito para ser resolvido'}}}
                                    theme={aggridUtils.generalTheme({rowHeight: 40, headerHeight: 40})}
                                />
                                <div className={`flex justify-end w-full ${styles.customTableBorderBottom}`}>
                                    <Button target="success" disabled={selectedType === "day" || substituteTableData.length === 0}
                                            onClick={() => {
                                                redirectToBatchPage()
                                            }}>Resolver em lote</Button>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <Card className="mt-2">
                    <Card.Body>
                        <div className="flex justify-end gap-2">
                            <Button target="danger" onClick={handleDelete} actionFeedbackType='delete'>
                                Excluir
                            </Button>
                            <Button id={"resolve-conflict"} disabled={!canSave}
                                    onClick={saveConflicts} actionFeedbackType='save'>
                                Salvar
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
                {
                    modalConflicts &&
                    <ModalConflictsResolution
                        open={modalConflicts}
                        setOpen={setModalConflicts}
                        id={conflictSelected}
                        updateTableWithNoReplace={updateTableWithNoReplace}
                        updateTable={updateTable}
                        conflictDefaultColDef={defaultColDef}
                        availableEffectives={conflictsById}
                    />
                }
            </TemplateBody>
        </Template>
    )
}

export default WorkScheduleConflicts